import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://gymmanagementbackend-tfh7.onrender.com/api',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 60000 // 60 seconds timeout (for Render cold starts)
});

// Add a request interceptor to add the auth token to every request
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle timeout errors (Render cold start)
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.warn('[API] Request timeout - server may be waking up from sleep');
      error.message = 'Server is starting up, please wait a moment and try again...';
    }
    
    // Handle 401 unauthorized errors
    if (error.response && error.response.status === 401) {
      // Membership expired while logged in: tell the member why on the login page
      if (error.response.data?.reason === 'membership_expired') {
        try {
          sessionStorage.setItem('loginNotice', error.response.data.message || 'Your membership has expired. Please contact the gym to renew.');
        } catch (e) {}
      }
      // Clear localStorage and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Authentication APIs
export const authAPI = {
  login: (credentials) => API.post('/auth/login', credentials),
  register: (userData) => API.post('/auth/register', userData),
  forgotPassword: (email) => API.post('/auth/forgot-password', { email }),
  resetPassword: (resetData) => API.post('/auth/reset-password', resetData),
  verifyToken: () => API.get('/auth/verify')
};

// User APIs
export const userAPI = {
  getAllUsers: () => API.get('/users'),
  getUserById: (id) => API.get(`/users/${id}`),
  createUser: (userData) => API.post('/users', userData),
  updateUser: (id, userData) => API.put(`/users/${id}`, userData),
  deleteUser: (id) => API.delete(`/users/${id}`),
  getProfile: () => API.get('/users/profile'),
  updateProfile: (userData) => API.put('/users/profile', userData),
  changePassword: (passwordData) => API.post('/users/change-password', passwordData)
};

// Member APIs
export const memberAPI = {
  getAllMembers: () => API.get('/members'),
  getMemberById: (id) => API.get(`/members/${id}`),
  createMember: (memberData) => API.post('/members', memberData),
  updateMember: (id, memberData) => API.put(`/members/${id}`, memberData),
  deleteMember: (id) => API.delete(`/members/${id}`),
  getProfile: () => API.get('/auth/profile'),
  bookAdvancePlan: (planData) => API.post('/members/advance-plan', planData),
  cancelAdvancePlan: (cancellationData) => API.post('/members/cancel-advance-plan', cancellationData),
  approveRenewal: (memberId, approvalData) => API.post(`/members/${memberId}/approve-renewal`, approvalData),
  processRefund: (memberId, refundData) => API.post(`/members/${memberId}/process-refund`, refundData)
};

// Membership Plan APIs
export const planAPI = {
  getAllPlans: () => API.get('/plans'),
  getPlanById: (id) => API.get(`/plans/${id}`),
  createPlan: (planData) => API.post('/plans', planData),
  updatePlan: (id, planData) => API.put(`/plans/${id}`, planData),
  deletePlan: (id) => API.delete(`/plans/${id}`),
  togglePlanStatus: (id) => API.patch(`/plans/${id}/toggle-status`),
  duplicatePlan: (id) => API.post(`/plans/${id}/duplicate`),
  getAnalytics: () => API.get('/plans/analytics/summary')
};

// Payment APIs
export const paymentAPI = {
  getAllPayments: () => API.get('/payments'),
  getPaymentById: (id) => API.get(`/payments/${id}`),
  createPayment: (paymentData) => API.post('/payments', paymentData),
  updatePayment: (id, paymentData) => API.put(`/payments/${id}`, paymentData),
  deletePayment: (id) => API.delete(`/payments/${id}`),
  getMemberPayments: (memberId) => API.get(`/payments/member/${memberId}`),
  sendReceipt: (id) => API.post(`/payments/${id}/send-receipt`),
  getPendingMembers: () => API.get('/payments/pending-members')
};

// Attendance APIs
export const attendanceAPI = {
  getAllAttendance: () => API.get('/attendance'),
  getAttendanceById: (id) => API.get(`/attendance/${id}`),
  checkIn: (memberId) => API.post('/attendance/check-in', { memberId }),
  checkOut: (attendanceId) => API.put(`/attendance/check-out/${attendanceId}`),
  getMemberAttendance: (memberId) => API.get(`/attendance/member/${memberId}`),
  getAttendanceByDateRange: (startDate, endDate) => API.get(`/attendance/range?startDate=${startDate}&endDate=${endDate}`),
  getMemberAnalytics: (memberId) => API.get(`/attendance/analytics/${memberId}`),
  getMemberQR: (memberId) => API.get(`/attendance/qr/${memberId}`),
  qrCheckIn: (token) => API.post('/attendance/qr-checkin', { token })
};

// Fingerprint (WebAuthn) attendance APIs
export const biometricAPI = {
  getStatus: () => API.get('/attendance/biometric/status'),
  registrationOptions: () => API.post('/attendance/biometric/register/options'),
  verifyRegistration: (data) => API.post('/attendance/biometric/register/verify', data),
  scanOptions: () => API.post('/attendance/biometric/scan/options'),
  verifyScan: (data) => API.post('/attendance/biometric/scan/verify', data),
  removeCredential: (id) => API.delete(`/attendance/biometric/credentials/${id}`)
};

// Product APIs
export const productAPI = {
  getAllProducts: () => API.get('/products'),
  getProductById: (id) => API.get(`/products/${id}`),
  createProduct: (data) => API.post('/products', data),
  updateProduct: (id, data) => API.put(`/products/${id}`, data),
  deleteProduct: (id) => API.delete(`/products/${id}`),
  getLowStockProducts: () => API.get('/products/alerts/low-stock'),
  uploadImages: (id, formData) => API.post(`/products/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (id, imageUrl) => API.delete(`/products/${id}/images`, { data: { imageUrl } })
};

// Sales APIs
export const salesAPI = {
  getAllSales: () => API.get('/sales'),
  getSaleById: (id) => API.get(`/sales/${id}`),
  createSale: (data) => API.post('/sales', data),
  getDailyReport: (date) => API.get(`/sales/reports/daily${date ? `?date=${date}` : ''}`),
  getSalesReport: (startDate, endDate) => API.get(`/sales/reports/summary?startDate=${startDate}&endDate=${endDate}`)
};

// Inventory (Stock movements, Purchase Orders, Analytics)
export const inventoryAPI = {
  // Stock
  stockIn: (data) => API.post('/inventory/stock-in', data),
  stockAdjust: (data) => API.post('/inventory/stock-adjust', data),
  getStockHistory: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return API.get(`/inventory/stock-history${q ? `?${q}` : ''}`);
  },
  // Purchase Orders
  getPurchaseOrders: (status) => API.get(`/inventory/purchase-orders${status ? `?status=${status}` : ''}`),
  createPurchaseOrder: (data) => API.post('/inventory/purchase-orders', data),
  receivePurchaseOrder: (id, data) => API.patch(`/inventory/purchase-orders/${id}/receive`, data),
  suggestReorders: () => API.get('/inventory/purchase-orders/suggest'),
  // Analytics
  getAnalytics: () => API.get('/inventory/analytics'),
};

// Product Booking APIs
export const productBookingAPI = {
  getAllBookings: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/product-bookings${query ? `?${query}` : ''}`);
  },
  getMemberBookings: (memberId) => API.get(`/product-bookings/member/${memberId}`),
  createBooking: (data) => API.post('/product-bookings', data),
  updateBookingStatus: (id, status) => API.patch(`/product-bookings/${id}/status`, { status }),
  updatePaymentStatus: (id, paymentStatus) => API.patch(`/product-bookings/${id}/payment`, { paymentStatus }),
  cancelBooking: (id) => API.delete(`/product-bookings/${id}`),
  getBookingStats: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/product-bookings/stats${query ? `?${query}` : ''}`);
  }
};

// Inventory Reports APIs
export const inventoryReportsAPI = {
  getSalesReport: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/inventory-reports/sales${query ? `?${query}` : ''}`);
  },
  getProfitLossReport: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/inventory-reports/profit-loss${query ? `?${query}` : ''}`);
  },
  getUserTransactionReport: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/inventory-reports/user-transactions${query ? `?${query}` : ''}`);
  },
  getStockSummary: () => API.get('/inventory-reports/stock-summary'),
  exportPDF: (reportType, params) => {
    const query = new URLSearchParams({ reportType, ...params }).toString();
    return API.get(`/inventory-reports/export-pdf?${query}`, { responseType: 'blob' });
  }
};

// Trainer Management APIs
export const trainerManagementAPI = {
  // Schedules
  getTrainerSchedules: (trainerId) => API.get(`/trainer-management/schedules${trainerId ? `?trainerId=${trainerId}` : ''}`),
  createTrainerSchedule: (data) => API.post('/trainer-management/schedules', data),
  updateTrainerSchedule: (id, data) => API.put(`/trainer-management/schedules/${id}`, data),
  deleteTrainerSchedule: (id) => API.delete(`/trainer-management/schedules/${id}`),
  
  // Assignments
  getTrainerAssignments: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/trainer-management/assignments${query ? `?${query}` : ''}`);
  },
  createTrainerAssignment: (data) => API.post('/trainer-management/assignments', data),
  updateTrainerAssignment: (id, data) => API.put(`/trainer-management/assignments/${id}`, data),
  
  // Sessions
  getTrainingSessions: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/trainer-management/sessions${query ? `?${query}` : ''}`);
  },
  createTrainingSession: (data) => API.post('/trainer-management/sessions', data),
  updateTrainingSession: (id, data) => API.put(`/trainer-management/sessions/${id}`, data),
  
  // Commissions
  getTrainerCommissions: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/trainer-management/commissions${query ? `?${query}` : ''}`);
  },
  createTrainerCommission: (data) => API.post('/trainer-management/commissions', data),
  markCommissionPaid: (id) => API.put(`/trainer-management/commissions/${id}/pay`)
};

// Member Dashboard APIs
export const memberDashboardAPI = {
  getPersonalDetails: () => API.get('/member/dashboard/personal'),
  getMembershipDetails: () => API.get('/member/dashboard/membership'),
  getPaymentHistory: () => API.get('/member/dashboard/payments'),
  getAttendanceHistory: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/member/dashboard/attendance${query ? `?${query}` : ''}`);
  },
  getTimeline: () => API.get('/member/dashboard/timeline'),
  getNotifications: () => API.get('/member/dashboard/notifications'),
  markNotificationRead: (id) => API.put(`/member/dashboard/notifications/${id}/read`),
  markAllNotificationsRead: () => API.put('/member/dashboard/notifications/read-all'),
};

// Member Product APIs
export const memberProductAPI = {
  getBookableProducts: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/member/products${query ? `?${query}` : ''}`);
  },
  createBooking: (data) => API.post('/member/bookings', data),
  getMyBookings: () => API.get('/member/bookings'),
  cancelBooking: (id) => API.patch(`/member/bookings/${id}/cancel`),

  // Reviews
  getProductReviews: (productId) => API.get(`/member/products/${productId}/reviews`),
  submitReview: (productId, data) => API.post(`/member/products/${productId}/reviews`, data),
  deleteReview: (productId) => API.delete(`/member/products/${productId}/reviews`),

  // Wishlist
  getWishlist: () => API.get('/member/wishlist'),
  getWishlistIds: () => API.get('/member/wishlist/ids'),
  toggleWishlist: (productId) => API.post(`/member/products/${productId}/wishlist`),

  // Stock notifications
  requestStockNotification: (productId) => API.post(`/member/products/${productId}/notify-stock`),
  getStockNotificationStatus: (productId) => API.get(`/member/products/${productId}/notify-stock`),
};

// Permission APIs
export const permissionAPI = {
  getPermissions: (params) => {
    const query = new URLSearchParams(params).toString();
    return API.get(`/permissions${query ? `?${query}` : ''}`);
  },
  updatePermissions: (data) => API.post('/permissions', data),
  getGymPermissions: (gymId) => API.get(`/permissions/gym/${gymId}`),
  getMenus: () => {
    // Get user from localStorage to extract gymId and role
    const authState = JSON.parse(localStorage.getItem('auth') || '{}');
    const user = authState.user || {};
    const gymId = user.gymId;
    const userType = user.role === 'member' ? 'member' : 'admin';
    console.log('🔐 getMenus - user:', user);
    console.log('🔐 getMenus - gymId:', gymId, 'userType:', userType);
    
    if (!gymId) {
      console.error('❌ No gymId found in user object!');
      return Promise.resolve({ data: { allowedMenus: [] } });
    }
    
    return API.get(`/permissions/menus?gymId=${gymId}&userType=${userType}`);
  }
};

// User Login Management APIs
export const userLoginAPI = {
  getMembersWithoutLogin: () => API.get('/user-login/members-without-login'),
  createMemberLogin: (data) => API.post('/user-login/create-member-login', data)
};

// Gym APIs
export const gymAPI = {
  getGymById: (gymId) => API.get(`/gyms/${gymId}`),
  updateGymSettings: (gymId, settings) => API.patch(`/gyms/${gymId}/settings`, settings)
};

// eSSL biometric machine (ADMS push) APIs — admin only
export const accessDeviceAPI = {
  getDevices: () => API.get('/access-devices/devices'),
  addDevice: (data) => API.post('/access-devices/devices', data),
  updateDevice: (id, data) => API.put(`/access-devices/devices/${id}`, data),
  removeDevice: (id) => API.delete(`/access-devices/devices/${id}`),
  runDeviceOperation: (id, operation) => API.post(`/access-devices/devices/${id}/operations`, { operation }),
  getSettings: () => API.get('/access-devices/settings'),
  updateSettings: (data) => API.put('/access-devices/settings', data),
  runChecks: () => API.post('/access-devices/checks/run'),
  getUnlinkedMachineUsers: () => API.get('/access-devices/machine-users', { params: { unlinked: 1 } }),
  getMemberAccess: (memberId) => API.get(`/access-devices/members/${memberId}`),
  // pin: optional existing machine User ID to link instead of allocating a new one
  enrollMember: (memberId, pin) => API.post(`/access-devices/members/${memberId}/enroll`, pin ? { pin } : undefined),
  blockMember: (memberId, reason) => API.post(`/access-devices/members/${memberId}/block`, { reason }),
  unblockMember: (memberId) => API.post(`/access-devices/members/${memberId}/unblock`)
};

// Host the eSSL machine must push to (Menu → Comm → Cloud Server Setting)
export const API_HOST = (() => {
  try {
    return new URL(API.defaults.baseURL).host;
  } catch {
    return 'gymmanagementbackend-tfh7.onrender.com';
  }
})();

// Trainer APIs
export const trainerAPI = {
  getAllTrainers: () => API.get('/trainers'),
  getTrainerById: (id) => API.get(`/trainers/${id}`),
  createTrainer: (trainerData) => API.post('/trainers', trainerData),
  updateTrainer: (id, trainerData) => API.put(`/trainers/${id}`, trainerData),
  deleteTrainer: (id) => API.delete(`/trainers/${id}`),
  // Booking APIs
  createBooking: (bookingData) => API.post('/trainers/bookings', bookingData),
  adminCreateBooking: (bookingData) => API.post('/trainers/bookings/admin', bookingData),
  getAllBookings: () => API.get('/trainers/bookings/all'),
  getMyBookings: () => API.get('/trainers/bookings/my'),
  verifyBooking: (id, verificationData) => API.patch(`/trainers/bookings/${id}/verify`, verificationData),
  upgradeBooking: (id, upgradeData) => API.patch(`/trainers/bookings/${id}/upgrade`, upgradeData)
};

// Analytics APIs (admin only) — base /api/analytics
export const analyticsAPI = {
  getChurn: () => API.get('/analytics/churn'),
  getRevenueForecast: () => API.get('/analytics/revenue-forecast'),
  getCohortRetention: () => API.get('/analytics/cohort-retention'),
  getAttendanceHeatmap: () => API.get('/analytics/attendance-heatmap'),
  getInsights: () => API.get('/analytics/insights')
};

// Ops APIs (admin only) — base /api/ops
export const opsAPI = {
  getWorklist: () => API.get('/ops/worklist'),
  logOutcome: (memberId, data) => API.post(`/ops/worklist/${memberId}/outcome`, data),
  getWhosIn: () => API.get('/ops/whos-in')
};

// AI Assistant APIs — base /api/ai
export const aiAssistantAPI = {
  ask: (question) => API.post('/ai/assistant', { question }),
  draftWinback: (memberId) => API.post('/ai/draft-winback', { memberId })
};

// WhatsApp APIs — base /api/whatsapp
export const whatsappAPI = {
  sendTest: (data) => API.post('/whatsapp/test', data)
};

// Renewal Journey APIs (admin) — base /api/renewal-journey
export const renewalJourneyAPI = {
  getSettings: () => API.get('/renewal-journey/settings'),
  updateSettings: (data) => API.put('/renewal-journey/settings', data),
  getLogs: (limit = 50) => API.get(`/renewal-journey/logs?limit=${limit}`),
  runNow: () => API.post('/renewal-journey/run-now')
};

// AI Progress APIs (member) — base /api/progress
export const progressAPI = {
  addLog: (data) => API.post('/progress', data),
  getMine: () => API.get('/progress/me'),
  getInsight: () => API.get('/progress/me/insight'),
  getMemberLogs: (memberId) => API.get(`/progress/member/${memberId}`)
};

export default API;

// ── Expense API ───────────────────────────────────────────────────────────────
export const expenseAPI = {
  getAll:          (params = {}) => API.get('/expenses', { params }),
  getById:         (id)          => API.get(`/expenses/${id}`),
  create:          (data)        => API.post('/expenses', data),
  update:          (id, data)    => API.put(`/expenses/${id}`, data),
  delete:          (id)          => API.delete(`/expenses/${id}`),
  getMonthlySummary: (year)      => API.get('/expenses/summary', { params: { year } })
};








// 🎯 Recommended Implementation Order:
// Phase 1 (Week 1-2): Quick Wins
// ✅ Announcement System (3 days)
// ✅ Success Stories & Testimonials (3 days)
// ✅ Keyboard Shortcuts (2 days)
// ✅ Expense Tracking (4 days)
// Phase 2 (Week 3-4): Core Features
// ✅ Invoice Generation (1 week)
// ✅ Lead Management System (1 week)
// ✅ Member Feedback & Surveys (4 days)
// Phase 3 (Week 5-6): Advanced Features
// ✅ In-app Chat (1.5 weeks)
// ✅ Email/SMS Campaigns (1 week)
// ✅ Referral Program (1 week)
// Phase 4 (Week 7-8): Financial & Analytics
// ✅ Profit & Loss Statements (1 week)
// ✅ Loyalty Points System (1 week)
// ✅ Dashboard Customization (1 week)
// Phase 5 (Week 9-10): Polish & Integration
// ✅ Multi-language Support (1 week)
// ✅ Advanced Search (1 week)
// ✅ Social Media Integration (1 week)